import { getOrg } from "utils/site";
import { useSignupSelector } from "../hooks";
import { Animal, Insurance, State } from "../state/types";
import { getAmountToPay } from "utils/insurance/getAmountToPay";
import { UpsertDraft_upsertDraft_policyPreviews_plans } from "../../../../../__generated__/UpsertDraft";
import { SignUpProductEnum } from "components/Signup/flow/SignupFlow/types";
import Router from "next/router";
import { parseUrl } from "components/Signup/flow/utils/parseUrlQuery";
import { assertNever } from "utils/typescript";
import { BillingInterval, YEARLY_BILLING_INTERVAL } from "utils/insurance";
import { createSelector } from "@reduxjs/toolkit";

export const getDraft = ({
  drafts,
  limit,
  deductible,
  copay,
  product,
  preventiveCareAddOnLimit,
  preexistingConditions,
}: {
  limit?: number;
  deductible?: number;
  copay?: number;
  product: SignUpProductEnum | null;
  drafts: UpsertDraft_upsertDraft_policyPreviews_plans[] | null | undefined;
  preventiveCareAddOnLimit?: number;
  preexistingConditions?: boolean | null;
}) => {
  const org = getOrg();

  switch (org) {
    case "swe":
      return drafts?.find(
        (preview) =>
          preview?.limit === limit &&
          preview.deductible === deductible &&
          preview.copay === copay &&
          preview.ratingsData?.preexistingConditions === preexistingConditions
      );
    case "ger": {
      if (product === SignUpProductEnum.health) {
        return drafts?.find(
          (preview) =>
            preview?.limit === limit &&
            preview.deductible === deductible &&
            preview.copay === copay
        );
      }

      if (product === SignUpProductEnum.liability) {
        return drafts?.find((preview) => preview?.deductible === deductible);
      }

      return null;
    }

    case "fra":
      return drafts?.find((preview) => {
        return (
          preview.limit === limit &&
          preview.coverages?.find(
            ({ coverageGroupId }) => coverageGroupId === "Preventive"
          )?.limit ===
            (preventiveCareAddOnLimit !== 0
              ? preventiveCareAddOnLimit
              : undefined) &&
          preview.copay === copay
        );
      });

    default:
      assertNever(org);
  }
};

export const getCurrentDraftForAnimal = (animal: Animal) => {
  const {
    limit,
    deductible,
    copay,
    drafts,
    product,
    preventiveCareAddOnLimit,
    preexistingConditions,
  } = animal.insurances[0];

  return getDraft({
    limit,
    deductible,
    copay,
    drafts,
    product,
    preventiveCareAddOnLimit,
    preexistingConditions,
  });
};

export const getCurrentDraftForInsurance = (insurance: Insurance) => {
  const {
    limit,
    deductible,
    copay,
    drafts,
    product,
    preventiveCareAddOnLimit,
    preexistingConditions,
  } = insurance;

  return getDraft({
    limit,
    deductible,
    copay,
    drafts,
    product,
    preventiveCareAddOnLimit,
    preexistingConditions,
  });
};

export const selectCurrentAnimal = (state: State) => {
  const animals = selectAllAnimals(state);

  let { animalId } = parseUrl(Router.query);

  const animalById = selectAnimalById(state, animalId as string);

  if (!animalById) {
    return animals[0];
  }

  return animalById;
};

export const useCurrentAnimal = () => useSignupSelector(selectCurrentAnimal);

export const selectAllAnimals = (state: State) => {
  return state.animals;
};

export const useAllAnimals = () => useSignupSelector(selectAllAnimals);

export const selectAllValidAnimals = createSelector(
  [selectAllAnimals],
  (animals) => {
    return animals.filter((animal) => !animal.isDraft);
  }
);
export const useAllValidAnimals = () =>
  useSignupSelector(selectAllValidAnimals);

export const selectBillingInterval = (state: State) => {
  return state.animals[0].insurances[0].billingInterval;
};

const sumDrafts = (
  drafts: (UpsertDraft_upsertDraft_policyPreviews_plans | null | undefined)[],
  billingInterval: number
) => {
  return drafts.reduce<{ new: number; old?: number }>(
    (sum, draft) => {
      const amountToPay = getAmountToPay({
        billingInterval,
        discounts: draft?.discounts,
        netPremium: draft?.netPremium,
        total: draft?.total,
      });

      return {
        new: sum.new + amountToPay.new,
        old: amountToPay.old
          ? (sum.old ?? 0) + amountToPay.old
          : sum.new + amountToPay.new,
      };
    },
    { new: 0, old: undefined }
  );
};

const calculateCostForAnimal = (animal: Animal, billingInterval: number) => {
  const drafts = animal.insurances.flatMap(getCurrentDraftForInsurance);

  return sumDrafts(drafts, billingInterval);
};

const calculateTotalCostForAllAnimals = (
  animals: Animal[],
  billingInterval: number
) => {
  const drafts = animals.flatMap((animal) =>
    animal.insurances.flatMap(getCurrentDraftForInsurance)
  );

  return sumDrafts(drafts, billingInterval);
};

export const selectTotalCost = createSelector(
  [selectAllValidAnimals, selectBillingInterval],
  (animals, billingInterval) => {
    return calculateTotalCostForAllAnimals(animals, billingInterval);
  }
);

export const useTotalCost = () =>
  useSignupSelector((state) => selectTotalCost(state));

export const selectTotalCostForAnimal = createSelector(
  [selectCurrentAnimal, selectBillingInterval],
  (animal, billingInterval) => {
    return calculateCostForAnimal(animal, billingInterval);
  }
);

export const useTotalCostForAnimal = () =>
  useSignupSelector((state) => selectTotalCostForAnimal(state));

export const selectTotalYearlyCost = createSelector(
  [selectAllValidAnimals, () => YEARLY_BILLING_INTERVAL],
  (animals, billingInterval) => {
    return calculateTotalCostForAllAnimals(animals, billingInterval);
  }
);

export const useTotalYearlyCost = () =>
  useSignupSelector((state) => selectTotalYearlyCost(state));

export const useBillingInterval = () =>
  useSignupSelector(selectBillingInterval);

export const selectBillingDay = (state: State) => {
  return state.animals[0].insurances[0].billingDay;
};

export const useBillingDay = () => useSignupSelector(selectBillingDay);

export const selectAnimalById = (state: State, id: string) => {
  return state.animals.find((pet) => pet.id === id);
};

export const useAnimalById = (id: string) =>
  useSignupSelector((state) => {
    const selectedAnimal = selectAnimalById(state, id);

    return selectedAnimal;
  });

export const selectTotalCostForAnimalById = createSelector(
  [selectAnimalById, selectBillingInterval],
  (animal, billingInterval) => {
    if (!animal) {
      return null;
    }
    return calculateCostForAnimal(animal, billingInterval);
  }
);

export const useTotalCostForAnimalById = (animalId: string) =>
  useSignupSelector((state) => selectTotalCostForAnimalById(state, animalId));

export const selectHasDrafts = (state: State) => {
  return selectAllValidAnimals(state).every(
    (pet) => (pet.insurances[0].drafts?.length ?? 0) > 0
  );
};

export const useHasDrafts = () => useSignupSelector(selectHasDrafts);

export const selectHasHealthInsurance = (state: State) =>
  selectAllValidAnimals(state)
    .flatMap((animal) => animal.insurances)
    .some((animal) => animal.product === SignUpProductEnum.health);

export const useHasHealthInsurance = () =>
  useSignupSelector(selectHasHealthInsurance);

export const selectHasLiabilityInsurance = (state: State) =>
  selectAllValidAnimals(state).some((animal) =>
    selectHasAnimalLiabilityInsurance(state, animal.id)
  );
export const useHasLiabilityInsurance = () =>
  useSignupSelector(selectHasLiabilityInsurance);

export const selectHasLiabilityAddon = (state: State) =>
  selectAllValidAnimals(state).some(
    (animal) =>
      animal.insurances.length > 1 &&
      animal.insurances.some(
        (ins) => ins.product === SignUpProductEnum.liability
      )
  );

export const useHasLiabilityAddon = () =>
  useSignupSelector(selectHasLiabilityAddon);

export const selectHasAnimalLiabilityInsurance = (
  state: State,
  animalId: string
) => {
  const animal = selectAllValidAnimals(state).find(
    (animal) => animal.id === animalId
  );

  if (animal) {
    return (
      (animal.insurances.filter(
        (insurance) => insurance.product === SignUpProductEnum.liability
      )?.length ?? 0) > 0
    );
  }

  return false;
};

export const useHasAnimalLiabilityInsurance = (animalId: string) =>
  useSignupSelector((state) =>
    selectHasAnimalLiabilityInsurance(state, animalId)
  );
